import StaffScanWaiting, { ScanLabel } from '../components/StaffScanWaiting';
/**
 * 여권취소 (tools) — QR 스캔 → 조회 → 확인 → 취소
 * ============================================================
 * 기획서 : docs/2026-08-25-passport-qr-registration-design.md §4.12.4(화면) · §4.12.6(문구)
 * dev-steps: 동 -dev-steps.md "### S17."
 *
 * 흐름 (§4.12.4)
 *   ① 스캔 대기  USB 리더기
 *   ② 조회 결과  여권 카드 + 쿠폰 목록·상태 + 회수/보존 미리보기
 *   ③ 취소 버튼  `cancelable` 일 때만 활성 — **서버 판정 그대로**
 *   ④ 확인 모달  대상 재표시 + 회수/보존 + 사유(선택) + 되돌릴 수 없음
 *   ⑤ 결과       실제 회수 4항목 + QR 재사용 불가 고지
 *
 * 🔴 **화면이 숫자를 계산하지 않습니다.** 회수 쿠폰·보존 쿠폰·주화·스탬프는 전부 서버
 *    응답값을 그대로 렌더합니다(CLAUDE.md 주의 8 — 적립·회수 수치 하드코딩 금지).
 *
 * 🔴 **가능여부도 서버가 판정합니다.** `cancelable`/`blocked_reason` 을 읽기만 하고
 *    QR·여권 상태 조합으로 다시 추론하지 않습니다 — 추론하면 서버 재판정과 어긋나
 *    "버튼은 눌리는데 취소에서만 403" 이 됩니다.
 *
 * 🔴 이 화면은 **QR 이 붙은 여권 전용**입니다(§4.12.5). POS 자동발급 여권·수동등록
 *    여권은 원장 행이 없어 스캔 자체가 불가하며, 주렁은행 올인원에서 취소합니다.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUsbQrReader from '../hooks/useUsbQrReader';
import { extractPassportQRCode } from '../utils/qrUtils';
import { getPassportByQr, cancelPassportByQr } from '../services/api';
import waitingImage from '../assets/passport/cancel-waiting.png';
import { Button, ConfigProvider, theme as antdTheme } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { ArrowLeftOutlined, ReloadOutlined, ExclamationCircleOutlined, CheckOutlined } from '@ant-design/icons';
import passportCover from '../assets/passport/passport-cover.svg';
import { useAuth } from '../contexts/AuthContext';
import './AllInOnePage.css';
import './BankWorkspace.css';
import './CouponRedeemPage.css';
import './PassportCancelPage.css';

/** 같은 코드 연속 스캔 차단. */
const SCAN_DEBOUNCE_MS = 1500;

/** 취소 사유 입력 상한 — 서버 `reason` 스키마(max_length=500)와 같은 값. */
const REASON_MAX_LENGTH = 500;

/**
 * 취소 차단 사유 문구 — 기획서 §4.12.2 표.
 * 🔴 키는 서버가 내려주는 `blocked_reason` 값입니다. 화면이 조건을 재구성하지 않습니다.
 */
const BLOCKED_MESSAGES = {
  QR_NOT_REGISTERED: '아직 등록되지 않은 여권 QR입니다. 고객 앱에서 등록 후 사용하세요.',
  QR_VOID: '폐기된 QR입니다. 취소할 수 있는 여권이 연결돼 있지 않습니다.',
  PASSPORT_MISSING: '여권 기록이 남아 있지 않습니다 — CRM 복구가 필요합니다.',
  PASSPORT_ALREADY_CANCELLED: '이미 취소된 여권입니다.',
  // 🔴 만료를 취소와 분리한다 (2026-09-01, 설계 D-6 단서 / dev-steps S9·S13).
  //    만료 전이 배치 도입 후 만료 여권은 EXPIRED 가 되는데, 이 키가 없으면 아래
  //    폴백('이 QR 은 취소할 수 없습니다.')이 떠서 만료라는 사실이 현장에 전달되지 않는다.
  PASSPORT_EXPIRED: '만료된 여권입니다. 만료 여권은 취소할 수 없습니다.',
};

const COUPON_STATUS_LABELS = {
  usable: '사용가능',
  used: '사용완료',
  expired: '만료',
  recalled: '회수됨',
};

const PASSPORT_STATUS_LABELS = {
  ACTIVE: '유효',
  EXPIRED: '만료',
  CANCELLED: '취소됨',
};


/** ISO 문자열에서 날짜만 잘라 씁니다(시각은 현장에서 의미가 없습니다). */
const toDate = (iso) => (iso ? String(iso).slice(0, 10) : '-');
const toPassportDate = (iso) => iso ? toDate(iso).replace(/-/g, '/') : '-';

/**
 * 서버 오류를 화면 문구로 바꿉니다.
 * `services/api.js` 인터셉터가 오류를 평면 객체로 정규화하므로 `err.message` 를 읽습니다
 * (`err.response.data.message` 가 아닙니다).
 */
const errorText = (err, fallback) => err?.message || fallback;

export default function PassportCancelPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const storeName = user?.store_name || localStorage.getItem('store_name') || '';

  const [view, setView] = useState(null);        // 조회 응답 그대로
  const [scanMessage, setScanMessage] = useState(null); // 스캔/조회 실패 안내
  const [looking, setLooking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [result, setResult] = useState(null);    // 취소 응답 그대로

  // 연속 스캔과 비동기 조회 사이의 중복 처리를 막기 위해 가드는 ref로 읽습니다.
  const lookingRef = useRef(false);
  const viewRef = useRef(null);
  const resultRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  const submittingRef = useRef(false);

  const applyView = useCallback((next) => {
    viewRef.current = next;
    setView(next);
  }, []);

  const applyResult = useCallback((next) => {
    resultRef.current = next;
    setResult(next);
  }, []);

  /** 다음 스캔을 받을 수 있게 화면을 비웁니다(연속 처리). */
  const resetForNextScan = useCallback(() => {
    applyView(null);
    applyResult(null);
    setScanMessage(null);
    setConfirmOpen(false);
    setReason('');
    setCancelError(null);
    lastScanRef.current = { code: '', at: 0 };
  }, [applyView, applyResult]);

  /** 여권 QR 1건 조회. 가드는 전부 ref(위 주석). */
  const handleScan = useCallback(async (rawCode) => {
    const code = extractPassportQRCode(rawCode);
    if (!code) {
      // USB 리더기의 입력은 여권 전용 파서로 검증합니다.
      setScanMessage('여권 QR이 아닙니다. 여권에 붙은 QR을 스캔해 주세요.');
      return;
    }
    if (lookingRef.current) return;
    if (viewRef.current || resultRef.current) return; // 이미 대상이 잡혀 있음

    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < SCAN_DEBOUNCE_MS) {
      return;
    }
    lastScanRef.current = { code, at: now };

    lookingRef.current = true;
    setLooking(true);
    setScanMessage(null);
    try {
      const data = await getPassportByQr(code);
      applyView(data);
    } catch (err) {
      applyView(null);
      setScanMessage(errorText(err, '여권 조회에 실패했습니다.'));
    } finally {
      lookingRef.current = false;
      setLooking(false);
    }
  }, [applyView]);

  // USB와 선택적으로 연 카메라 모두 동일한 여권 파서/조회 가드를 사용합니다.
  // 조회가 끝났거나 진행 중이면 새 버퍼를 받지 않습니다.
  useUsbQrReader({
    enabled: !view && !result && !looking,
    onScan: handleScan,
    minLength: 8,
  });

  const openConfirm = () => {
    setCancelError(null);
    setReason('');
    setConfirmOpen(true);
  };

  /** 실제 취소 — in-flight 잠금(값 회수라 더블탭 중복 요청을 막습니다). */
  const handleCancel = async () => {
    if (submittingRef.current || submitting) return;
    if (!view?.cancelable || !view?.passport) return;

    submittingRef.current = true;
    setSubmitting(true);
    setCancelError(null);
    try {
      const data = await cancelPassportByQr(view.code, {
        // 🔴 조회 때 본 대상을 함께 보냅니다 — 스캔과 클릭 사이에 다른 운영자가
        //    처리했으면 서버가 409 로 끊습니다.
        expectedPassportId: view.passport.passport_id,
        reason,
      });
      setConfirmOpen(false);
      applyResult({ ...data, owner_name: view.passport.owner_name });
    } catch (err) {
      setCancelError(errorText(err, '취소에 실패했습니다.'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const preview = view?.cancel_preview;
  const blockedMessage = view?.blocked_reason
    ? (BLOCKED_MESSAGES[view.blocked_reason] || '이 QR 은 취소할 수 없습니다.')
    : null;

  const activeStamps = useMemo(() => {
    if (!view?.passport) return 0;
    // 스탬프는 취소 시 지워지지 않고 회수 표시만 되므로(S5/D9) 유효분은 차로 구합니다.
    return Math.max(0, (view.passport.stamp_count || 0) - (view.passport.stamp_recalled_count || 0));
  }, [view]);

  return (
    <ConfigProvider locale={koKR} theme={{ algorithm: antdTheme.defaultAlgorithm, token: {
      colorPrimary: '#007800', colorBgContainer: '#ffffff', colorBgElevated: '#ffffff',
      colorBgLayout: '#f5f5f5', colorText: '#0d0d0d', colorTextSecondary: '#666666',
      colorBorder: '#dcdcdc', colorSuccess: '#189018', borderRadius: 6,
      fontFamily: '"Noto Sans KR", sans-serif',
    } }}>
    <div className="pc-page bank-workspace-page cr-page">
      <header className="all-header">
        <div className="all-header-left">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>메뉴</Button>
          <span className="all-header-title">여권 취소</span>
        </div>
        <div className="all-header-right">
          {view && !result && <Button icon={<ReloadOutlined />} onClick={resetForNextScan} disabled={confirmOpen || submitting} data-testid="passport-rescan">다시 스캔</Button>}
          {storeName && <span className="bank-store-badge"><i aria-hidden="true" />{storeName}</span>}
          <span className="all-user-name"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
        </div>
      </header>

      {!view && !result && (
        <StaffScanWaiting image={waitingImage} alt="여권 QR을 스캔하는 주렁 프렌즈와 탐험가" busy={looking} onScan={handleScan} testId="passport-waiting" error={scanMessage} guide={<><ScanLabel>주렁주렁 여권</ScanLabel><span>–</span><span className="ssw-phrase"><ScanLabel>QR</ScanLabel><span>을 스캔해주세요.</span></span></>} />
      )}
      <div className="pc-content" data-result={Boolean(result)} hidden={!view && !result}>
      <section className="pc-right">
        {result ? (
          <ResultPanel result={result} onNext={resetForNextScan} />
        ) : !view ? null : (
          <>
            <div className={`pc-passport-card pc-passport-state-${(view.passport?.status || 'none').toLowerCase()}`}>
              <div className="pc-passport-head"><h2>여권 정보</h2>
                <span className="pc-passport-status-label" data-testid="passport-status">
                  {view.passport?.status === 'CANCELLED' ? '취소여권' : view.passport?.status === 'EXPIRED' ? '만료 여권' : view.passport?.status === 'ACTIVE' ? '유효 여권' : (PASSPORT_STATUS_LABELS[view.passport?.status] || view.passport?.status || '여권 없음')}
                </span>
              </div>
              <div className="pc-passport-content">
                <div className="pc-passport-art"><img src={passportCover} alt="주렁주렁 여권" /></div>
                <div className="pc-owner-block">
                  <span>소유자</span>
                  <strong>{view.passport?.owner_name || '-'}</strong>
                </div>
              </div>
              <dl className="pc-passport-meta">
                <div className="pc-meta-store"><dt>지점</dt><dd>{view.passport?.store_name || view.qr?.store_name || '-'}</dd></div>
                <div><dt>스탬프</dt><dd>{activeStamps}개
                  {view.passport?.stamp_recalled_count > 0 && <small className="pc-muted"> (회수 {view.passport.stamp_recalled_count}개)</small>}
                </dd></div>
                <div className="pc-meta-validity"><dt>유효기간</dt><dd>{toPassportDate(view.passport?.issue_date)} ~ {toPassportDate(view.passport?.expires_at)}</dd></div>
              </dl>
            </div>

            <div className="pc-coupon-column">
            <div className="pc-coupons">
              <div className="pc-coupons-head">
                <h2>발급 쿠폰</h2>
                <span className="pc-coupons-caption">여권 취소 시 미사용 쿠폰만 회수됩니다</span>
              </div>
              {preview && (
                <section className="pc-benefit-review" aria-label="쿠폰 회수 및 보존 안내">
                  <div className="pc-preview-summary">
                    <div className="pc-preview-recall"><div><span>회수 대상</span><small>미사용 쿠폰</small></div><strong>{preview.coupons_to_recall}<small>건</small></strong></div>
                    <div className="pc-preview-preserve"><div><span>보존</span><small>사용완료/만료</small></div><strong>{preview.coupons_preserved}<small>건</small></strong></div>
                  </div>
                </section>
              )}
              {view.coupons?.length ? (
                <div className="pc-table-wrap">
                  <table className="pc-table">
                    <thead>
                      <tr>
                        <th>쿠폰명</th><th>상태</th><th>유효기간</th><th>사용일</th>
                      </tr>
                    </thead>
                    <tbody>
                      {view.coupons.map((c) => (
                        <tr key={c.issue_id} className={c.status === 'usable' ? 'pc-coupon-to-recall' : undefined}>
                          <td>{c.name}</td>
                          <td>
                            <span className={`pc-chip pc-chip-${c.status}`}>
                              {COUPON_STATUS_LABELS[c.status] || c.status}
                            </span>
                          </td>
                          <td className="pc-coupon-validity">{toPassportDate(c.issued_at)} ~ {toPassportDate(c.expires_at)}</td>
                          <td>{toDate(c.used_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="pc-coupons-empty">이 여권으로 발급된 쿠폰이 없습니다.</p>
              )}

            </div>
            <div className="pc-actions">
              {blockedMessage && view.blocked_reason !== 'PASSPORT_ALREADY_CANCELLED' && (
                <p className="pc-blocked" role="alert">{blockedMessage}</p>
              )}
              <div className="pc-actions-buttons">
                <button
                  type="button"
                  className="pc-btn-danger"
                  disabled={!view.cancelable}
                  onClick={openConfirm}
                >
                  여권 취소
                </button>
              </div>
            </div>
            </div>
          </>
        )}
      </section>
      </div>

      {/* ───── ④ 확인 모달 (§4.12.6 (나)) ───── */}
      {confirmOpen && view?.passport && (
        <div className="pc-modal-backdrop">
          <div className="pc-modal" role="dialog" aria-modal="true" aria-labelledby="pc-cancel-title">
            <div className="pc-modal-heading">
              <h2 className="pc-modal-title" id="pc-cancel-title">여권 취소</h2>
            </div>

            <div className="pc-modal-target">
              <div className="pc-modal-owner">
                <div><span>소유자</span><strong>{view.passport.owner_name}</strong></div>
              </div>
              <dl className="pc-modal-target-meta">
                <div>
                  <dt>지점</dt>
                  <dd>{view.passport.store_name || '-'}</dd>
                </div>
                <div>
                  <dt>유효기간</dt>
                  <dd>{toPassportDate(view.passport.issue_date)} ~ {toPassportDate(view.passport.expires_at)}</dd>
                </div>
              </dl>
              <div className="pc-modal-coupon-summary">
                <div>
                  <span>회수</span>
                  <strong>{preview?.coupons_to_recall ?? 0}<span className="pc-modal-count-unit">건</span></strong>
                </div>
                <div>
                  <span>보존</span>
                  <strong>{preview?.coupons_preserved ?? 0}<span className="pc-modal-count-unit">건</span></strong>
                </div>
              </div>
            </div>

            {/* 🔴 §4.12.6 확정 본문 — 파기를 뜻하는 낱말을 쓰지 않습니다.
                실제로 지워지는 행이 하나도 없습니다(여권 CANCELLED · 쿠폰 recalled · 스탬프 마킹). */}
            <div className="pc-modal-body">
              <p className="pc-modal-body-title">회수 혜택</p>
              <ul>
                <li><CheckOutlined aria-hidden="true" /><div>미사용 쿠폰</div></li>
                <li><CheckOutlined aria-hidden="true" /><div>여권 구매 주화</div></li>
                <li><CheckOutlined aria-hidden="true" /><div>이 여권의 스탬프</div></li>
              </ul>
            </div>

            <label className="pc-modal-reason">
              <span className="pc-modal-reason-title">
                <strong>취소 사유</strong>
                <em>선택</em>
              </span>
              <textarea
                value={reason}
                maxLength={REASON_MAX_LENGTH}
                rows={2}
                placeholder="예시) 고객 요청으로 인한 취소"
                onChange={(e) => setReason(e.target.value)}
              />
              <span className="pc-modal-reason-count">{reason.length} / {REASON_MAX_LENGTH}</span>
            </label>


            {cancelError && <p className="pc-modal-error" role="alert">{cancelError}</p>}

            <div className="pc-modal-footer">
              <p className="pc-modal-warn"><ExclamationCircleOutlined aria-hidden="true" /><span>취소 후에는 되돌릴 수 없습니다.</span></p>
            <div className="pc-modal-actions">
              <button
                type="button"
                className="pc-btn-secondary"
                disabled={submitting}
                onClick={() => setConfirmOpen(false)}
              >
                닫기
              </button>
              <button
                type="button"
                className="pc-btn-danger"
                disabled={submitting}
                onClick={handleCancel}
              >
                {submitting ? '처리 중…' : '취소 진행'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </ConfigProvider>
  );
}

/**
 * ⑤ 결과 화면 (§4.12.6 (다)).
 * 🔴 네 숫자 모두 **서버 응답값**입니다. 화면이 미리보기 값을 재사용하지 않습니다 —
 *    보너스 3종 회수는 취소가 커밋된 뒤 판정되므로 미리보기와 다를 수 있습니다.
 */
function ResultPanel({ result, onNext }) {
  return (
    <div className="pc-result">
      <div className="pc-result-heading" role="status">
        <div className="pc-completion-emblem" aria-hidden="true">
          <span className="pc-completion-ring" />
          <span className="pc-completion-spark pc-completion-spark-one" />
          <span className="pc-completion-spark pc-completion-spark-two" />
          <span className="pc-completion-spark pc-completion-spark-three" />
          <span className="pc-result-check"><svg viewBox="0 0 48 48" fill="none"><path className="pc-completion-tick" pathLength="1" d="m12 24 8 8 17-18" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
        </div>
        <h2 className="pc-result-title">여권 취소 완료</h2>
      </div>
      <section className="pc-result-card" aria-label="여권 취소 처리 결과">
        <div className="pc-result-customer"><span>고객명</span><strong>{result.owner_name || '-'}</strong></div>
        <dl className="pc-result-metrics">
          <div><dt>회수 쿠폰</dt><dd>{result.recalled_coupons}<small>개</small></dd></div>
          <div><dt>회수 주화</dt><dd>{result.reversed_coins}<small>개</small></dd></div>
          <div><dt>회수 스탬프</dt><dd>{result.recalled_stamps}<small>개</small></dd></div>
        </dl>
      </section>
      <p className="pc-result-guidance"><ExclamationCircleOutlined aria-hidden="true" /><span>취소된 여권의 QR을 재사용할 수 없습니다.</span></p>
      <button type="button" className="pc-btn-primary pc-result-next" onClick={onNext}>다음 여권</button>
    </div>
  );
}
